import { api, get, uploadDocument } from "../api.ts";
import { rerender } from "../rerender.ts";
import { isAdmin, state, wsBase } from "../state.ts";
import type { Overview, TrackRow } from "../types.ts";
import { append, busy, confirmDialog, h, toast, when } from "../ui.ts";

/** Everyone the caller may see, ignoring the overview's filters. */
const everyone = () => get<Overview>(`${wsBase()}/overview`);

function assignForm(tracks: TrackRow[], o: Overview) {
  const track = h("select", { "aria-label": "Track", required: true }) as HTMLSelectElement;
  for (const t of tracks) track.append(h("option", { value: t.id }, t.title));
  const group = h("select", { "aria-label": "Group" }, h("option", { value: "" }, "No group")) as HTMLSelectElement;
  for (const g of o.groups) group.append(h("option", { value: g.id }, g.path));
  const due = h("input", { type: "date", "aria-label": "Due date" }) as HTMLInputElement;
  const people = h("fieldset", { class: "pick" }, h("legend", null, "Or pick people"));
  for (const p of o.people.filter((x) => !x.suspended)) {
    people.append(h("label", { class: "check" }, h("input", { type: "checkbox", value: p.user_id }), p.name ?? p.email));
  }
  const give = h("button", { type: "submit", class: "primary" }, "Give track") as HTMLButtonElement;
  const form = h(
    "form",
    { class: "stack" },
    h("div", { class: "form-row" }, h("label", { class: "field" }, "Track", track), h("label", { class: "field" }, "To a whole group", group), h("label", { class: "field" }, "Due date", due)),
    people,
    h("p", { class: "muted small" }, "People added to the group later get the track too."),
    h("div", { class: "actions" }, give),
  );
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const ids = Array.from(people.querySelectorAll<HTMLInputElement>("input:checked")).map((x) => x.value);
    if (!group.value && ids.length === 0) return toast("Pick a group or at least one person.", "error");
    void busy(give, async () => {
      const r = await api<{ assignments: unknown[] }>("POST", `${wsBase()}/assignments`, {
        track: track.value,
        user_ids: ids,
        ...(group.value ? { group_id: group.value } : {}),
        due_date: due.value || null,
      });
      toast(`Track given to ${r.assignments.length} ${r.assignments.length === 1 ? "person" : "people"}.`);
      state.overview = null;
      rerender();
    });
  });
  return form;
}

function assignmentList(o: Overview) {
  const rows = h("tbody");
  for (const p of o.people) {
    for (const t of p.tracks) {
      const a = t.assignment;
      if (!a) continue;
      const due = h("input", { type: "date", value: a.due_date ?? "", "aria-label": `Due date for ${p.email}, ${t.title}` }) as HTMLInputElement;
      due.addEventListener("change", () =>
        void api("PATCH", `${wsBase()}/assignments/${a.id}`, { due_date: due.value || null })
          .then(() => toast("Due date saved."))
          .catch((e: Error) => toast(e.message, "error")),
      );
      const take = h("button", { type: "button", class: "small danger" }, "Withdraw") as HTMLButtonElement;
      take.addEventListener("click", () =>
        busy(take, async () => {
          if (!(await confirmDialog(`Withdraw ${t.title} from ${p.name ?? p.email}?`, "It leaves their list and stops counting as overdue. What they already finished stays.", "Withdraw track", true))) return;
          await api("DELETE", `${wsBase()}/assignments/${a.id}`);
          toast("Track withdrawn.");
          state.overview = null;
          rerender();
        }),
      );
      rows.append(
        h(
          "tr",
          null,
          h("td", null, p.name ?? p.email),
          h("td", null, t.title, h("div", { class: "muted small" }, `${t.modules_done} of ${t.modules_total} modules${a.status === "done" ? ", finished" : ""}`)),
          h("td", { class: a.overdue ? "late" : "" }, due, a.overdue && h("div", { class: "small" }, `overdue since ${when(a.due_date)}`)),
          h("td", { class: "row-actions" }, take),
        ),
      );
    }
  }
  if (!rows.childElementCount) return h("p", { class: "empty" }, "No tracks given yet.");
  return h("div", { class: "table-wrap" }, h("table", null, h("thead", null, h("tr", null, h("th", null, "Person"), h("th", null, "Track"), h("th", null, "Due"), h("th", null, h("span", { class: "sr" }, "Actions")))), rows));
}

function companyTrackForm() {
  const title = h("input", { type: "text", maxlength: 120, placeholder: "dbt at our company", "aria-label": "Track title" }) as HTMLInputElement;
  const file = h("input", { type: "file", accept: ".pdf,.txt,.md,application/pdf,text/plain,text/markdown", "aria-label": "Syllabus file" }) as HTMLInputElement;
  const make = h("button", { type: "submit", class: "primary" }, "Make track from syllabus") as HTMLButtonElement;
  const form = h(
    "form",
    { class: "stack" },
    h("div", { class: "form-row" }, h("label", { class: "field" }, "Title (optional)", title), h("label", { class: "field" }, "Syllabus (PDF, text or Markdown, up to 10 MB)", file)),
    h("p", { class: "muted small" }, "Kai reads the syllabus once and turns it into modules with a lab and a checkpoint each. Uploading the same title again makes a new version."),
    h("div", { class: "actions" }, make),
  );
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const f = file.files?.[0];
    if (!f) return toast("Choose the syllabus file first.", "error");
    void busy(make, async () => {
      make.textContent = "Reading the syllabus…";
      try {
        const doc = await uploadDocument(f);
        const r = await api<{ track: { title: string; modules: unknown[] } }>("POST", `${wsBase()}/tracks`, {
          document_id: doc.id,
          ...(title.value.trim() ? { title: title.value.trim() } : {}),
        });
        toast(`${r.track.title} is ready with ${r.track.modules.length} modules.`);
        rerender();
      } finally {
        make.textContent = "Make track from syllabus";
      }
    });
  });
  return form;
}

export async function renderTracks(root: HTMLElement) {
  const [{ tracks }, o] = await Promise.all([get<{ tracks: TrackRow[] }>(`${wsBase()}/tracks`), everyone()]);
  const body = h("tbody");
  for (const t of tracks) {
    const del = !t.built_in && isAdmin() ? (h("button", { type: "button", class: "small danger" }, "Delete") as HTMLButtonElement) : null;
    del?.addEventListener("click", () =>
      busy(del, async () => {
        if (!(await confirmDialog(`Delete ${t.title}?`, `It is withdrawn from the ${t.assigned} ${t.assigned === 1 ? "person" : "people"} who have it. Their finished modules stay in their history.`, "Delete track", true))) return;
        await api("DELETE", `${wsBase()}/tracks/${encodeURIComponent(t.id)}`);
        toast(`${t.title} deleted.`);
        state.overview = null;
        rerender();
      }),
    );
    body.append(
      h(
        "tr",
        null,
        h(
          "td",
          null,
          h("div", { class: "who" }, t.title),
          h("div", { class: "muted small" }, [t.built_in ? "Built in" : `Your company's, version ${t.version}`, t.certification && `prepares for ${t.certification}`, t.status === "draft" && "draft outline"].filter(Boolean).join(", ")),
        ),
        h("td", { class: "num" }, t.modules.length),
        h("td", { class: "num" }, t.assigned),
        h("td", { class: "num" }, t.done),
        h("td", { class: t.overdue ? "num late" : "num" }, t.overdue),
        h("td", { class: "row-actions" }, del),
      ),
    );
  }
  append(
    root,
    h("header", { class: "page-head" }, h("div", null, h("h1", null, "Tracks"), h("p", { class: "muted" }, isAdmin() ? "Give tracks, set due dates, and take them back." : "Give tracks to people in your groups."))),
    h(
      "div",
      { class: "table-wrap" },
      h("table", null, h("thead", null, h("tr", null, h("th", null, "Track"), h("th", { class: "num" }, "Modules"), h("th", { class: "num" }, "Given to"), h("th", { class: "num" }, "Finished"), h("th", { class: "num" }, "Overdue"), h("th", null, h("span", { class: "sr" }, "Actions")))), body),
    ),
    h("section", null, h("h2", null, "Give a track"), assignForm(tracks, o)),
    h("section", null, h("h2", null, "Who has which track"), assignmentList(o)),
    isAdmin() && h("section", null, h("h2", null, "Add your company's own track"), companyTrackForm()),
  );
}
